/**
 * Tracking state machine + user guidance.
 *
 *   idle ─start→ searching ─face→ acquiring ─(2 frames)→ tracking
 *   tracking ─no face→ coasting ─(> coastMs)→ lost ─face→ acquiring
 *
 * `opacity()` provides the fade used by the renderer: fade-in on acquisition,
 * hold during coasting, fade-out on loss. Filters are reset when the loss
 * lasted long enough that continuity would look wrong (a frame sliding in
 * from its last known position).
 */
export type TrackingState = 'idle' | 'searching' | 'acquiring' | 'tracking' | 'coasting' | 'lost';

export type Hint = 'none' | 'no_face' | 'too_dark' | 'too_far' | 'too_close' | 'look_at_camera' | 'multiple_faces' | 'hold_still';

export interface TrackingObservation {
  /** A face was found in this inference. */
  faceDetected: boolean;
  /** Mean luminance of the frame, 0..255 (or null when not measured). */
  luma: number | null;
  /** Face width as a fraction of the video's smaller dimension (0..1). */
  faceWidthRatio: number;
  /** Iris diameter in pixels (0 when no face). */
  irisPx: number;
  /** Number of faces reported by the tracker. */
  faceCount: number;
  /** True when the pose solver produced a pose (depth calibrated). */
  poseReady: boolean;
  /** Time in ms (monotonic). */
  nowMs: number;
}

export interface StateMachineParams {
  /** Consecutive detections required before showing the frame. */
  acquireFrames: number;
  /** Extrapolate this long after the last detection before declaring loss. */
  coastMs: number;
  /** Fade durations. */
  fadeInMs: number;
  fadeOutMs: number;
  /** Losses longer than this reset the smoothing filters on re-acquisition. */
  resetAfterMs: number;
  /** Guidance thresholds. */
  darkLuma: number;
  tooFarIrisPx: number;
  tooCloseFaceRatio: number;
  hintDelayMs: number;
}

export const DEFAULT_STATE_PARAMS: StateMachineParams = {
  acquireFrames: 2,
  coastMs: 160,
  fadeInMs: 180,
  fadeOutMs: 220,
  resetAfterMs: 500,
  darkLuma: 45,
  tooFarIrisPx: 7,
  tooCloseFaceRatio: 0.92,
  hintDelayMs: 700,
};

export class TrackingStateMachine {
  readonly params: StateMachineParams;
  private state: TrackingState = 'idle';
  private stateSince = 0;
  private lastSeenMs = -Infinity;
  private consecutive = 0;
  private lastObservationMs = -Infinity;
  private inferenceIntervalMs = 0;
  private lostAtMs = -Infinity;
  private shouldReset = false;
  private hint: Hint = 'none';
  private hintCandidate: Hint = 'none';
  private hintCandidateSince = 0;
  /** Counters for analytics. */
  readonly stats = { losses: 0, reacquisitions: 0, lastReacquireMs: 0 };

  constructor(params: Partial<StateMachineParams> = {}) {
    this.params = { ...DEFAULT_STATE_PARAMS, ...params };
  }

  start(nowMs: number): void {
    this.lastObservationMs = -Infinity;
    this.inferenceIntervalMs = 0;
    this.consecutive = 0;
    this.transition('searching', nowMs);
  }

  stop(nowMs: number): void {
    this.transition('idle', nowMs);
    this.consecutive = 0;
  }

  get current(): TrackingState {
    return this.state;
  }

  get currentHint(): Hint {
    return this.hint;
  }

  /** Whether the smoother should be reset before feeding the next sample (consumed on read). */
  consumeResetFlag(): boolean {
    const r = this.shouldReset;
    this.shouldReset = false;
    return r;
  }

  /** Feed one inference result. Returns the new state. */
  update(obs: TrackingObservation): TrackingState {
    const now = obs.nowMs;
    if (this.state === 'idle') return this.state;
    if (Number.isFinite(this.lastObservationMs)) {
      const interval = Math.max(0, now - this.lastObservationMs);
      this.inferenceIntervalMs = Math.max(interval, this.inferenceIntervalMs * 0.9);
    }
    this.lastObservationMs = now;

    if (obs.faceDetected && obs.poseReady) {
      this.consecutive++;
      const wasLost = this.state === 'lost' || this.state === 'searching';
      if (wasLost) {
        if (this.state === 'lost') {
          this.stats.reacquisitions++;
          this.stats.lastReacquireMs = now - this.lostAtMs;
          if (now - this.lastSeenMs > this.params.resetAfterMs) this.shouldReset = true;
        }
        this.transition('acquiring', now);
      } else if (this.state === 'coasting') {
        this.transition('tracking', now);
      }
      if (this.state === 'acquiring' && this.consecutive >= this.params.acquireFrames) {
        this.transition('tracking', now);
      }
      this.lastSeenMs = now;
    } else {
      this.consecutive = 0;
      if (this.state === 'tracking' || this.state === 'acquiring') {
        this.transition('coasting', now);
      }
      if (this.state === 'coasting' && now - this.lastSeenMs > this.params.coastMs) {
        this.stats.losses++;
        this.lostAtMs = now;
        this.transition('lost', now);
      }
    }

    this.updateHint(obs);
    return this.state;
  }

  /** Called once per rendered frame so the coast timeout fires even without new inferences. */
  tick(nowMs: number): TrackingState {
    if (this.state === 'coasting' && nowMs - this.lastSeenMs > this.params.coastMs) {
      this.stats.losses++;
      this.lostAtMs = nowMs;
      this.transition('lost', nowMs);
    }
    // A stalled worker is not evidence that the face disappeared. Hold the
    // last pose through ordinary inference pauses, then fade a truly stale pose.
    if ((this.state === 'tracking' || this.state === 'acquiring') &&
        nowMs - this.lastObservationMs > Math.max(1000, 5 * this.inferenceIntervalMs)) {
      this.lostAtMs = nowMs;
      this.transition('lost', nowMs);
    }
    return this.state;
  }

  /** Render opacity of the frame at time `nowMs` (0..1). */
  opacity(nowMs: number): number {
    const p = this.params;
    switch (this.state) {
      case 'tracking':
      case 'coasting': {
        // Fade in from acquisition start.
        const since = nowMs - this.acquiredAtMs;
        return clamp01(since / p.fadeInMs);
      }
      case 'acquiring':
        return 0;
      case 'lost': {
        const since = nowMs - this.stateSince;
        return clamp01(1 - since / p.fadeOutMs);
      }
      default:
        return 0;
    }
  }

  private acquiredAtMs = 0;

  private transition(next: TrackingState, nowMs: number): void {
    if (next === this.state) return;
    if (next === 'acquiring') this.acquiredAtMs = nowMs;
    this.state = next;
    this.stateSince = nowMs;
  }

  private updateHint(obs: TrackingObservation): void {
    let candidate: Hint = 'none';
    if (!obs.faceDetected) {
      if (obs.luma !== null && obs.luma < this.params.darkLuma) candidate = 'too_dark';
      else candidate = 'no_face';
    } else if (obs.faceCount > 1) {
      candidate = 'multiple_faces';
    } else if (obs.irisPx > 0 && obs.irisPx < this.params.tooFarIrisPx) {
      candidate = 'too_far';
    } else if (obs.faceWidthRatio > this.params.tooCloseFaceRatio) {
      candidate = 'too_close';
    } else if (!obs.poseReady) {
      candidate = 'look_at_camera';
    } else if (obs.luma !== null && obs.luma < this.params.darkLuma) {
      candidate = 'too_dark';
    }

    // Debounce: a hint must persist before it is shown; clearing is immediate.
    if (candidate === 'none') {
      this.hint = 'none';
      this.hintCandidate = 'none';
      return;
    }
    if (candidate !== this.hintCandidate) {
      this.hintCandidate = candidate;
      this.hintCandidateSince = obs.nowMs;
    }
    if (obs.nowMs - this.hintCandidateSince >= this.params.hintDelayMs || this.hint === candidate) {
      this.hint = candidate;
    }
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
